// Create/edit form for a payment request — used by /requests/new and /requests/:id/edit.
//
// 3-step mobile wizard:
//   ① Location  — request type (vendor payment / internal transfer, ACL-gated) + legal entity
//                 + operation unit (both required; each preselected when only one).
//   ② Items     — amount-line cards (department → budget → approver cascade, tag, amount,
//                 remarks, invoices). Departments are scoped to the step-① operation unit.
//   ③ Vendor    — vendor search (scoped to the step-① OU), PAN, mode of payment (bank modes
//                 show the vendor's bank accounts to pick from), priority, note, disposal
//                 instruction, review card and submit.
//   ③ Transfer from (internal transfers) — source department + budget the total is moved out
//                 of (same OU-scoped cascade as step ②), read-only transfer amount (the server
//                 sets it to the request total), priority, note, review card and submit. No
//                 vendor, mode of payment, bank details or disposal instruction — the server
//                 ignores/nulls them for internal transfers.
//
// Request type: shown only when the user holds both ACL grants (segmented toggle); with one
// grant the type is fixed; with neither the form is replaced by a no-permission card. In edit
// mode the type comes from initial.reqType and is locked. Switching type on a new request
// keeps step-①/② data and clears the type-specific step-③ data.
//
// Cascade: department → budget items (apiBudgetItemsOfDepartment, with available balance) →
// approvers + tags (apiApproversOfBudget). apiInitData supplies myOperationUnits directly, and
// myDepartments carry operationUnitId (the "(OU name)" suffix match remains only as a fallback
// for stale cached init data).
//
// Changing the operation unit after later steps have data clears the dependent selections
// (department/budget/approver on every line, and the vendor). Changing legal entity keeps lines.
//
// Edit mode MUST post back existing amount-line ids and attachment {id, filePath} — the server
// soft-deletes any row whose id is missing from the payload (full-matrix authoritative save).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { toast } from "sonner";
import { CheckIcon, ChevronsUpDownIcon, CircleAlert, Plus, Trash2 } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInitData } from "@/lib/init-data";
import { fold } from "@/lib/search";
import {
  fetchApproversOfBudget,
  fetchBudgetItemsOfDepartment,
  fetchVendorBankAccounts,
  formatAmount,
  isInternalTransferType,
  REQ_TYPE_INTERNAL_TRANSFER_ID,
  saveRequest,
  searchVendors,
  type Approver,
  type BudgetItem,
  type MyDepartment,
  type NamedItem,
  type PaymentRequestAmountLine,
  type PaymentRequestDetail,
  type SaveAmountLinePayload,
  type SaveRequestPayload,
  type VendorBankAccount,
  type VendorOption,
} from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { InvoiceCapture, type AttachmentChip } from "@/components/invoice-capture";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

// ---------------------------------------------------------------------------
// Line state
// ---------------------------------------------------------------------------

interface FormLine {
  key: number;
  /** Existing PaymentRequestAmount id (edit mode). */
  id?: number;
  /**
   * The saved amount + budget of the line as loaded (edit mode). While the same budget is
   * still selected, the original amount is added to the available balance — the server adds
   * the old amount back when re-validating an edit.
   */
  originalAmount?: number;
  originalBudgetId?: number;
  departmentId: number | null;
  budgetId: number | null;
  approverId: number | null;
  tag: string | null;
  amount: string;
  remarks: string;
  attachments: AttachmentChip[];
  /** null = not loaded yet. */
  budgetItems: BudgetItem[] | null;
  budgetLoading: boolean;
  approvers: Approver[] | null;
  tags: string[];
  approverLoading: boolean;
}

let lineSeq = 0;

function emptyLine(): FormLine {
  return {
    key: ++lineSeq,
    departmentId: null,
    budgetId: null,
    approverId: null,
    tag: null,
    amount: "",
    remarks: "",
    attachments: [],
    budgetItems: null,
    budgetLoading: false,
    approvers: null,
    tags: [],
    approverLoading: false,
  };
}

function linesFromDetail(d: PaymentRequestDetail): FormLine[] {
  const lines = (d.listPaymentRequestAmount ?? []).map((l) => ({
    ...emptyLine(),
    id: l.id,
    originalAmount: l.amount ?? undefined,
    originalBudgetId: l.expenseBudget?.id ?? undefined,
    departmentId: l.department?.id ?? null,
    budgetId: l.expenseBudget?.id ?? null,
    approverId: l.reqApprovedBy?.id ?? null,
    tag: l.tag ?? null,
    amount: l.amount != null ? String(l.amount) : "",
    remarks: l.remarks ?? "",
    attachments: (l.listPaymentRequestAttachment ?? [])
      .filter((a) => a.filePath)
      .map((a) => ({ id: a.id, filePath: a.filePath as string })),
  }));
  return lines.length > 0 ? lines : [emptyLine()];
}

/** Split the server's HTML-ish error string into plain lines (never rendered as HTML). */
function splitErrors(errors: string): string[] {
  return errors
    .split(/<br\s*\/?>/i)
    .map((s) => s.replace(/<[^>]*>/g, "").trim())
    .filter(Boolean);
}

const enabled = (b: BudgetItem) => !!b.paymentRequestEnabled;

/**
 * The maximum amount the line may request, or null while it cannot be determined (no budget
 * selected, or its data not loaded yet — never block on missing data). Allowed max is the
 * budget's available balance, plus the line's original saved amount in edit mode while the
 * same budget is still selected (the server adds the old amount back when re-validating).
 */
function lineAllowedMax(l: FormLine): number | null {
  if (l.budgetId == null) return null;
  const budget = (l.budgetItems ?? []).find((b) => b.id === l.budgetId);
  if (!budget || budget.availableAmount == null) return null;
  const original =
    l.originalAmount != null && l.originalBudgetId === l.budgetId ? l.originalAmount : 0;
  return budget.availableAmount + original;
}

function isLineOverBalance(l: FormLine): boolean {
  const max = lineAllowedMax(l);
  if (max == null) return false;
  const amount = Number(l.amount);
  return !!l.amount.trim() && !Number.isNaN(amount) && amount > max;
}

const isLineComplete = (l: FormLine) => {
  const amount = Number(l.amount);
  return (
    l.departmentId != null &&
    l.budgetId != null &&
    l.approverId != null &&
    !!l.amount.trim() &&
    !Number.isNaN(amount) &&
    amount > 0 &&
    !isLineOverBalance(l)
  );
};

/** Does the department belong to the operation unit? (id match; name-suffix fallback). */
function deptInOperationUnit(d: MyDepartment, ouId: number, ouName: string | null | undefined): boolean {
  if (d.operationUnitId != null) return d.operationUnitId === ouId;
  // Fallback for entries lacking operationUnitId: match the "(OU name)" suffix in "Dept (OU)".
  const m = d.name?.match(/\(([^()]*)\)\s*$/);
  return !!m && !!ouName && fold(m[1]) === fold(ouName);
}

// ---------------------------------------------------------------------------
// Wizard steps
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3;

/** Request kind — "regular" is a vendor payment, "internal" a budget transfer. */
type ReqKind = "regular" | "internal";

const KIND_LABELS: Record<ReqKind, string> = {
  regular: "Vendor payment",
  internal: "Internal transfer",
};

function stepLabels(kind: ReqKind): Record<Step, string> {
  return { 1: "Location", 2: "Items", 3: kind === "internal" ? "Transfer from" : "Vendor" };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RequestForm({
  mode,
  initial,
}: {
  mode: "new" | "edit";
  /** The apiGetRequest .paymentRequest detail (edit mode). */
  initial?: PaymentRequestDetail;
}) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const initData = useInitData();

  // --- Request type ---
  // Edit mode: the type comes from the loaded request and is locked (switching the type of an
  // existing request is not supported). New mode: the user's choice, defaulted from the grants.
  const editIsInternal =
    mode === "edit" &&
    isInternalTransferType(initial?.reqType, initData.reqTypeInternalTransferId);
  const [chosenKind, setChosenKind] = useState<ReqKind | null>(null);
  const kind: ReqKind =
    mode === "edit"
      ? editIsInternal
        ? "internal"
        : "regular"
      : (chosenKind ??
        (initData.canMakeRegularRequest || !initData.canMakeInternalTransfer
          ? "regular"
          : "internal"));
  const isInternal = kind === "internal";

  // --- Step ① Location ---
  const [legalEntityId, setLegalEntityId] = useState<number | null>(initial?.legalEntity?.id ?? null);
  const [operationUnitId, setOperationUnitId] = useState<number | null>(
    initial?.operationUnit?.id ?? null
  );

  // --- Step ③ Vendor & submit ---
  const [priorityId, setPriorityId] = useState<number | null>(initial?.priority?.id ?? null);
  const [modeOfPaymentId, setModeOfPaymentId] = useState<number | null>(initial?.modeOfPayment?.id ?? null);
  const [vendor, setVendor] = useState<VendorOption | null>(
    initial?.vendor?.id
      ? {
          id: initial.vendor.id,
          name: initial.vendor.name ?? "",
          pan: initial.vendor.pan ?? null,
          email: null,
          verifiedForPayment: false,
        }
      : null
  );
  const [vendorPan, setVendorPan] = useState(initial?.vendorPan ?? initial?.vendor?.pan ?? "");
  const [note, setNote] = useState(initial?.reqNote ?? "");

  // Vendor bank accounts (bank payment modes only). null = not loaded (or load failed).
  const [bankAccounts, setBankAccounts] = useState<VendorBankAccount[] | null>(null);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<number | null>(
    initial?.vendorBankAccountRef?.id ?? null
  );

  // --- Step ③ Transfer from (internal transfers) ---
  // The saved transferFrom of the request as loaded (edit mode). While the SAME source budget
  // is still selected, its original amount is added back to the available balance — the server
  // does the same when re-validating an edit. `id` must be posted back so the row is updated.
  const initialTransferFrom = useMemo(
    () =>
      initial?.paymentRequestTransferFrom
        ? {
            id: initial.paymentRequestTransferFrom.id ?? null,
            amount: initial.paymentRequestTransferFrom.amount ?? 0,
            budgetId: initial.paymentRequestTransferFrom.expenseBudget?.id ?? null,
          }
        : null,
    [initial]
  );
  const [srcDepartmentId, setSrcDepartmentId] = useState<number | null>(
    initial?.paymentRequestTransferFrom?.department?.id ?? null
  );
  const [srcBudgetId, setSrcBudgetId] = useState<number | null>(
    initial?.paymentRequestTransferFrom?.expenseBudget?.id ?? null
  );
  /** null = not loaded yet (or no department selected). */
  const [srcBudgetItems, setSrcBudgetItems] = useState<BudgetItem[] | null>(null);
  const [srcBudgetLoading, setSrcBudgetLoading] = useState(false);

  // --- Step ② Item lines ---
  const [lines, setLines] = useState<FormLine[]>(() =>
    initial ? linesFromDetail(initial) : [emptyLine()]
  );

  const [step, setStep] = useState<Step>(1);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(0);
  const errorsRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Default the legal entity / operation unit when there is exactly one.
  useEffect(() => {
    if (legalEntityId == null && initData.legalEntities.length === 1) {
      setLegalEntityId(initData.legalEntities[0].id);
    }
  }, [initData.legalEntities, legalEntityId]);
  useEffect(() => {
    if (operationUnitId == null && initData.myOperationUnits.length === 1) {
      setOperationUnitId(initData.myOperationUnits[0].id);
    }
  }, [initData.myOperationUnits, operationUnitId]);

  // Bank payment mode? Server sends string ids, option ids are numbers — String() both sides.
  const isBankMode =
    modeOfPaymentId != null &&
    initData.bankModeOfPaymentIds.some((id) => String(id) === String(modeOfPaymentId));

  // Fetch the vendor's bank accounts whenever a vendor is picked with a bank payment mode.
  const vendorId = vendor?.id ?? null;
  useEffect(() => {
    if (!token || vendorId == null || !isBankMode) return;
    const controller = new AbortController();
    setBankAccountsLoading(true);
    fetchVendorBankAccounts(token, vendorId, controller.signal)
      .then((accounts) => {
        setBankAccounts(accounts);
        // Keep a still-valid selection (edit prefill); a single account is preselected.
        setBankAccountId((prev) =>
          prev != null && accounts.some((a) => a.id === prev)
            ? prev
            : accounts.length === 1
              ? accounts[0].id
              : null
        );
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        // Leave bankAccounts null — the "no account on record" notice must not show on errors.
        toast.error(e instanceof ApiError ? e.message : "Could not load vendor bank accounts.");
      })
      .finally(() => setBankAccountsLoading(false));
    return () => controller.abort();
  }, [token, vendorId, isBankMode]);

  // Load the source-budget items whenever a source department is selected (internal transfers).
  // Also hydrates the edit-mode prefill; until it lands the combobox shows the injected fallback.
  useEffect(() => {
    if (!token || srcDepartmentId == null) {
      setSrcBudgetItems(null);
      return;
    }
    const controller = new AbortController();
    setSrcBudgetLoading(true);
    fetchBudgetItemsOfDepartment(token, srcDepartmentId, controller.signal)
      .then((items) => setSrcBudgetItems(items ?? []))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setSrcBudgetItems([]);
        toast.error(e instanceof ApiError ? e.message : "Could not load budget items.");
      })
      .finally(() => setSrcBudgetLoading(false));
    return () => controller.abort();
  }, [token, srcDepartmentId]);

  // Switching the request type (new requests only) keeps step-①/② data but clears the
  // type-specific step-③ data of BOTH types, so a later switch back starts clean.
  const onKindChange = (next: ReqKind) => {
    if (mode === "edit" || next === kind) return;
    setChosenKind(next);
    // Vendor-payment step-③ data.
    setVendor(null);
    setVendorPan("");
    setModeOfPaymentId(null);
    setBankAccounts(null);
    setBankAccountId(null);
    // Internal-transfer step-③ data.
    setSrcDepartmentId(null);
    setSrcBudgetId(null);
    setSrcBudgetItems(null);
  };

  // Retry init data once if the session-level load failed earlier.
  const reloadedRef = useRef(false);
  useEffect(() => {
    if (!initData.loaded && !reloadedRef.current) {
      reloadedRef.current = true;
      initData.reload();
    }
  }, [initData]);

  // Keep the request's own OU selectable in edit mode even if it is missing from my list.
  const operationUnitOptions = useMemo<NamedItem[]>(() => {
    const list = [...initData.myOperationUnits];
    const initialOuId = initial?.operationUnit?.id;
    if (initialOuId != null && !list.some((o) => o.id === initialOuId)) {
      list.unshift({ id: initialOuId, name: initial?.operationUnit?.name ?? `Unit #${initialOuId}` });
    }
    return list;
  }, [initData.myOperationUnits, initial]);

  const selectedOperationUnit = useMemo(
    () => operationUnitOptions.find((o) => o.id === operationUnitId) ?? null,
    [operationUnitOptions, operationUnitId]
  );

  // Departments available in the step-① operation unit.
  const departmentsInUnit = useMemo<MyDepartment[]>(() => {
    if (operationUnitId == null) return [];
    return initData.myDepartments.filter((d) =>
      deptInOperationUnit(d, operationUnitId, selectedOperationUnit?.name)
    );
  }, [initData.myDepartments, operationUnitId, selectedOperationUnit]);

  // --- Line helpers ---

  const patchLine = useCallback((key: number, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }, []);

  const loadBudgets = useCallback(
    (key: number, departmentId: number) => {
      if (!token) return;
      patchLine(key, { budgetLoading: true });
      fetchBudgetItemsOfDepartment(token, departmentId)
        .then((items) => patchLine(key, { budgetItems: items ?? [], budgetLoading: false }))
        .catch((e) => {
          patchLine(key, { budgetItems: [], budgetLoading: false });
          toast.error(e instanceof ApiError ? e.message : "Could not load budget items.");
        });
    },
    [token, patchLine]
  );

  const loadApprovers = useCallback(
    (key: number, budgetItemId: number) => {
      if (!token) return;
      patchLine(key, { approverLoading: true });
      fetchApproversOfBudget(token, budgetItemId)
        .then((d) =>
          patchLine(key, {
            approvers: d?.approvers ?? [],
            tags: d?.tags ?? [],
            approverLoading: false,
          })
        )
        .catch((e) => {
          patchLine(key, { approvers: [], tags: [], approverLoading: false });
          toast.error(e instanceof ApiError ? e.message : "Could not load approvers.");
        });
    },
    [token, patchLine]
  );

  // Edit mode: hydrate the cascades for the prefilled lines (once, on mount).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !token) return;
    hydratedRef.current = true;
    lines.forEach((l) => {
      if (l.departmentId && l.budgetItems === null) loadBudgets(l.key, l.departmentId);
      if (l.budgetId && l.approvers === null) loadApprovers(l.key, l.budgetId);
    });
  }, [token, lines, loadBudgets, loadApprovers]);

  const onDepartmentChange = (line: FormLine, departmentId: number | null) => {
    patchLine(line.key, {
      departmentId,
      budgetId: null,
      approverId: null,
      tag: null,
      budgetItems: null,
      approvers: null,
      tags: [],
    });
    if (departmentId) loadBudgets(line.key, departmentId);
  };

  const onBudgetChange = (line: FormLine, budgetId: number | null) => {
    patchLine(line.key, { budgetId, approverId: null, tag: null, approvers: null, tags: [] });
    if (budgetId) loadApprovers(line.key, budgetId);
  };

  const addLine = () => {
    setLines((prev) => {
      const last = prev[prev.length - 1];
      const next = emptyLine();
      // Convenience: new lines start on the previous line's department (list already loaded).
      if (last?.departmentId) {
        next.departmentId = last.departmentId;
        next.budgetItems = last.budgetItems;
      }
      return [...prev, next];
    });
  };

  const removeLine = (key: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));
  };

  // Changing the operation unit invalidates every OU-scoped selection made in later steps.
  const onOperationUnitChange = (id: number | null) => {
    if (id === operationUnitId) return;
    const hadDependentData =
      vendor != null ||
      srcDepartmentId != null ||
      lines.some((l) => l.departmentId != null || l.budgetId != null || l.approverId != null);
    setOperationUnitId(id);
    if (operationUnitId != null && hadDependentData) {
      setLines((prev) =>
        prev.map((l) => ({
          ...l,
          departmentId: null,
          budgetId: null,
          approverId: null,
          tag: null,
          budgetItems: null,
          approvers: null,
          tags: [],
        }))
      );
      setVendor(null);
      setVendorPan("");
      setBankAccounts(null);
      setBankAccountId(null);
      // The transfer source is OU-scoped too (same-location rule on the server).
      setSrcDepartmentId(null);
      setSrcBudgetId(null);
      setSrcBudgetItems(null);
      toast.info(
        isInternal
          ? "Location changed — re-select departments and source budget"
          : "Location changed — re-select departments and vendor"
      );
    }
  };

  // --- Step validity ---

  const total = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

  const step1Valid = legalEntityId != null && operationUnitId != null;
  const step2Valid =
    departmentsInUnit.length > 0 && lines.length > 0 && lines.every(isLineComplete);

  // Internal transfers: the SOURCE budget must cover the step-② total. Allowed max is its
  // available balance, plus the original transfer amount in edit mode while the same source
  // budget is still selected (the server adds the old amount back when re-validating).
  // null while it cannot be determined (nothing selected / data not loaded) — never block
  // on missing data.
  const selectedSrcBudget = (srcBudgetItems ?? []).find((b) => b.id === srcBudgetId) ?? null;
  const srcAllowedMax =
    isInternal && srcBudgetId != null && selectedSrcBudget?.availableAmount != null
      ? selectedSrcBudget.availableAmount +
        (initialTransferFrom && initialTransferFrom.budgetId === srcBudgetId
          ? initialTransferFrom.amount
          : 0)
      : null;
  const srcOverBalance = srcAllowedMax != null && total > srcAllowedMax;

  const step3Valid = isInternal
    ? srcDepartmentId != null && srcBudgetId != null && priorityId != null && !srcOverBalance
    : vendor != null && modeOfPaymentId != null && priorityId != null;

  // A line over its budget's available balance — or a source budget that cannot cover the
  // transfer — freezes the wizard entirely (Next, Back and stepper jumps) until corrected.
  const anyLineOverBalance = lines.some(isLineOverBalance);
  const wizardFrozen = anyLineOverBalance || srcOverBalance;

  const canGoTo = (target: Step): boolean => {
    if (wizardFrozen) return false;
    if (target <= step) return true;
    if (target >= 2 && !step1Valid) return false;
    if (target >= 3 && !step2Valid) return false;
    return true;
  };

  const goTo = (target: Step) => {
    if (!canGoTo(target)) return;
    setStep(target);
    requestAnimationFrame(() => topRef.current?.scrollIntoView({ block: "start" }));
  };

  // --- Submit ---

  const validate = (): string[] => {
    const errors: string[] = [];
    if (legalEntityId == null) errors.push("Select a legal entity.");
    if (operationUnitId == null) errors.push("Select a location.");
    if (isInternal) {
      if (srcDepartmentId == null) errors.push("Select the department to transfer from.");
      if (srcBudgetId == null) errors.push("Select the source budget to transfer from.");
    } else {
      if (!vendor) errors.push("Select a vendor.");
      if (modeOfPaymentId == null) errors.push("Select a mode of payment.");
    }
    if (priorityId == null) errors.push("Select a priority.");
    if (lines.length === 0) errors.push("Add at least one amount line.");
    lines.forEach((l, i) => {
      const n = `Line ${i + 1}`;
      if (l.departmentId == null) errors.push(`${n}: select a department.`);
      if (l.budgetId == null) errors.push(`${n}: select a budget.`);
      if (l.approverId == null) errors.push(`${n}: select an approver.`);
      const amount = Number(l.amount);
      if (!l.amount.trim() || Number.isNaN(amount) || amount <= 0) {
        errors.push(`${n}: enter an amount greater than zero.`);
      }
    });
    return errors;
  };

  const showErrors = (errors: string[]) => {
    setFormErrors(errors);
    // Errors about budgets/balances/departments belong to step ② — jump back there. For an
    // internal transfer those words also describe the step-③ source budget, so stay put and
    // let the error box (rendered on every step) point the way.
    if (!isInternal && errors.some((e) => /budget|balance|department/i.test(e))) {
      setStep(2);
    }
    requestAnimationFrame(() => errorsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const submit = () => {
    if (!token) return;
    const errors = validate();
    if (errors.length > 0) {
      showErrors(errors);
      return;
    }
    setFormErrors([]);
    const listPaymentRequestAmount = lines.map<SaveAmountLinePayload>((l) => ({
      ...(l.id ? { id: l.id } : {}),
      department: { id: l.departmentId as number },
      expenseBudget: { id: l.budgetId as number },
      reqApprovedBy: { id: l.approverId as number },
      amount: Number(l.amount),
      ...(l.remarks.trim() ? { remarks: l.remarks.trim() } : {}),
      ...(l.tag ? { tag: l.tag } : {}),
      listPaymentRequestAttachment: l.attachments.map((a) => ({
        ...(a.id ? { id: a.id } : {}),
        filePath: a.filePath,
      })),
    }));
    // Internal transfers carry reqType + paymentRequestTransferFrom and NO vendor/mode/bank/
    // disposal fields (the server ignores/nulls them). The regular payload stays exactly as
    // before — no reqType key (the server defaults a missing type to a regular request).
    const payload: SaveRequestPayload = isInternal
      ? {
          ...(mode === "edit" && initial ? { id: initial.id } : {}),
          reqType: {
            id: initData.reqTypeInternalTransferId ?? REQ_TYPE_INTERNAL_TRANSFER_ID,
          },
          priority: { id: priorityId as number },
          legalEntity: { id: legalEntityId as number },
          reqNote: note.trim(),
          paymentRequestTransferFrom: {
            // Post the existing row id back in edit mode so the server updates it in place.
            ...(initialTransferFrom?.id ? { id: initialTransferFrom.id } : {}),
            expenseBudget: { id: srcBudgetId as number },
          },
          listPaymentRequestAmount,
        }
      : {
          ...(mode === "edit" && initial ? { id: initial.id } : {}),
          priority: { id: priorityId as number },
          legalEntity: { id: legalEntityId as number },
          modeOfPayment: { id: modeOfPaymentId as number },
          vendor: { id: (vendor as VendorOption).id },
          // Only bank payment modes carry a bank-account pick (server validates ownership).
          ...(isBankMode && bankAccountId != null
            ? { vendorBankAccountRef: { id: bankAccountId } }
            : {}),
          ...(vendorPan.trim() ? { vendorPan: vendorPan.trim() } : {}),
          // disposalInstruction is deliberately NOT sent: the field was dropped from this UI,
          // and omitting the key leaves the stored value untouched on edits (bindData skips
          // absent keys).
          reqNote: note.trim(),
          listPaymentRequestAmount,
        };
    setSaving(true);
    saveRequest(token, payload)
      .then((r) => {
        if (r.errors) {
          showErrors(splitErrors(r.errors));
          return;
        }
        toast.success(r.myMessage || "Saved successfully.");
        navigate(r.ID ? `/requests/${r.ID}` : "/requests", { replace: true });
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          showErrors([
            e.status === 401
              ? "Your session has expired — please sign in again."
              : "You are not allowed to save this request (only the requester can edit it).",
          ]);
        } else {
          showErrors([e instanceof Error ? e.message : "Could not save the request."]);
        }
      })
      .finally(() => setSaving(false));
  };

  const onUploadBusyChange = useCallback((busy: boolean) => {
    setUploadBusy((c) => Math.max(0, c + (busy ? 1 : -1)));
  }, []);

  if (!initData.loaded) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">Loading form data…</div>
    );
  }

  // Neither grant → no form at all (the server would 403 the save anyway).
  if (!initData.canMakeRegularRequest && !initData.canMakeInternalTransfer) {
    return (
      <div className="p-4">
        <Card size="sm">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <CircleAlert className="size-8 text-muted-foreground" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                You don&apos;t have permission to raise payment requests
              </p>
              <p className="text-xs text-muted-foreground">
                Please contact the accounts team if you believe you should have access.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate("/")}>
              Back to home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // New mode always shows both types as radio options (the ungranted one disabled); the
  // neither-grant case already returned the no-permission card above. Edit mode shows a
  // locked static label instead — the type of an existing request cannot change.
  const showKindToggle = mode === "new";
  const showKindStatic = mode === "edit";

  const nextDisabled =
    wizardFrozen || (step === 1 ? !step1Valid : step === 2 ? !step2Valid : false);

  // Source cascade options (internal transfers). Edit mode keeps the prefilled department/
  // budget displayable while the OU list / cascade has not produced them (same pattern as the
  // amount-line fallbacks).
  const srcDepartmentOptions: ComboboxOption[] = departmentsInUnit.map((d) => ({
    id: d.id,
    name: d.name ?? d.label ?? String(d.id),
  }));
  const initialSrcDept = initial?.paymentRequestTransferFrom?.department;
  if (
    srcDepartmentId != null &&
    !srcDepartmentOptions.some((o) => o.id === srcDepartmentId) &&
    initialSrcDept?.id === srcDepartmentId
  ) {
    srcDepartmentOptions.unshift({
      id: srcDepartmentId,
      name: initialSrcDept?.name ?? `Department #${srcDepartmentId}`,
    });
  }

  const srcBudgetOptions: ComboboxOption[] = (srcBudgetItems ?? [])
    .filter(enabled)
    .map((b) => ({ id: b.id, name: b.name }));
  const initialSrcBudget = initial?.paymentRequestTransferFrom?.expenseBudget;
  if (
    srcBudgetId != null &&
    !srcBudgetOptions.some((o) => o.id === srcBudgetId) &&
    initialSrcBudget?.id === srcBudgetId
  ) {
    srcBudgetOptions.unshift({
      id: srcBudgetId,
      name: initialSrcBudget?.name ?? `Budget #${srcBudgetId}`,
    });
  }

  return (
    <div className="flex min-h-full flex-col">
      <Stepper step={step} labels={stepLabels(kind)} canGoTo={canGoTo} onGo={goTo} />

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div ref={topRef} className="scroll-mt-20" aria-hidden="true" />

        {formErrors.length > 0 && (
          <div
            ref={errorsRef}
            className="scroll-mt-24 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <div className="mb-1 flex items-center gap-1.5 font-medium">
              <CircleAlert className="size-4 shrink-0" /> Please fix the following
            </div>
            <ul className="flex list-disc flex-col gap-0.5 pl-5">
              {formErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {step === 1 && (
          <Card size="sm">
            <CardHeader>
              <CardTitle className="text-sm">Legal entity &amp; location</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {showKindToggle && (
                <Field label="Request type">
                  <div role="radiogroup" aria-label="Request type" className="grid grid-cols-2 gap-1.5">
                    {(["regular", "internal"] as ReqKind[]).map((k) => {
                      const allowed =
                        k === "regular"
                          ? initData.canMakeRegularRequest
                          : initData.canMakeInternalTransfer;
                      const selected = kind === k;
                      return (
                        <Button
                          key={k}
                          type="button"
                          variant="outline"
                          role="radio"
                          aria-checked={selected}
                          disabled={!allowed}
                          className={cn(
                            "h-auto justify-start gap-2 px-3 py-2.5 text-sm font-medium",
                            selected && "border-primary bg-primary/5"
                          )}
                          onClick={() => allowed && onKindChange(k)}
                        >
                          <span
                            className={cn(
                              "flex size-4 shrink-0 items-center justify-center rounded-full border",
                              selected ? "border-primary" : "border-muted-foreground/50"
                            )}
                          >
                            {selected && <span className="size-2 rounded-full bg-primary" />}
                          </span>
                          {KIND_LABELS[k]}
                          {!allowed && (
                            <span className="ml-auto text-xs font-normal text-muted-foreground">
                              No access
                            </span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </Field>
              )}
              {showKindStatic && (
                <Field
                  label="Request type"
                  hint={mode === "edit" ? "The type of an existing request cannot be changed." : undefined}
                >
                  <p className="text-sm font-medium">{KIND_LABELS[kind]}</p>
                </Field>
              )}

              <Field label="Legal entity">
                <IdSelect
                  options={initData.legalEntities}
                  value={legalEntityId}
                  onChange={setLegalEntityId}
                  placeholder="Select legal entity…"
                />
              </Field>

              <Field
                label="Location / Operation unit"
                hint={
                  operationUnitOptions.length === 0
                    ? "You are not assigned to any operation unit."
                    : undefined
                }
              >
                <IdSelect
                  options={operationUnitOptions}
                  value={operationUnitId}
                  onChange={onOperationUnitChange}
                  placeholder="Select location…"
                />
              </Field>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <>
            {departmentsInUnit.length === 0 && (
              <div className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-100 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <CircleAlert className="size-4 shrink-0" /> You have no departments in this location
              </div>
            )}

            {lines.map((line, i) => (
              <LineCard
                key={line.key}
                index={i}
                line={line}
                token={token ?? ""}
                departments={departmentsInUnit}
                initialLine={initial?.listPaymentRequestAmount?.find((l) => l.id === line.id)}
                canRemove={lines.length > 1}
                onDepartmentChange={(id) => onDepartmentChange(line, id)}
                onBudgetChange={(id) => onBudgetChange(line, id)}
                onPatch={(patch) => patchLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
                onUploadBusyChange={onUploadBusyChange}
              />
            ))}

            <Button type="button" variant="outline" onClick={addLine}>
              <Plus /> Add amount line
            </Button>

            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Total requested</span>
              <span className="font-semibold">{formatAmount(total)}</span>
            </div>
          </>
        )}

        {step === 3 && isInternal && (
          <>
            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">Transfer from</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Field label="Department">
                  <Combobox
                    options={srcDepartmentOptions}
                    value={srcDepartmentId}
                    onValueChange={(id) => {
                      setSrcDepartmentId(id);
                      setSrcBudgetId(null);
                      setSrcBudgetItems(null);
                    }}
                    placeholder="Select department…"
                    emptyText="No departments in this location."
                  />
                </Field>

                <Field
                  label="Source budget"
                  hint={
                    srcBudgetLoading
                      ? "Loading budget items…"
                      : selectedSrcBudget
                        ? `Available balance: ${formatAmount(selectedSrcBudget.availableAmount)}`
                        : srcDepartmentId == null
                          ? "Select a department first."
                          : undefined
                  }
                  error={
                    srcOverBalance && srcAllowedMax != null
                      ? `Source budget balance ${formatAmount(srcAllowedMax)} is insufficient for this transfer of ${formatAmount(total)}`
                      : undefined
                  }
                >
                  <Combobox
                    options={srcBudgetOptions}
                    value={srcBudgetId}
                    onValueChange={setSrcBudgetId}
                    placeholder={srcBudgetLoading ? "Loading…" : "Select source budget…"}
                    disabled={srcDepartmentId == null || srcBudgetLoading}
                    emptyText="No requestable budget items."
                  />
                </Field>

                <Field label="Transfer amount" hint="Set automatically to the request total.">
                  <Input
                    value={formatAmount(total)}
                    readOnly
                    className="text-muted-foreground"
                  />
                </Field>

                <Field label="Priority">
                  <IdSelect
                    options={initData.priorities}
                    value={priorityId}
                    onChange={setPriorityId}
                    placeholder="Select priority…"
                  />
                </Field>

                <Field label="Note">
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What is this transfer for?"
                    rows={3}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">Review</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-1.5 text-sm">
                <ReviewRow
                  label="Legal entity"
                  value={
                    initData.legalEntities.find((o) => o.id === legalEntityId)?.name ??
                    initData.legalEntities.find((o) => o.id === legalEntityId)?.label ??
                    initial?.legalEntity?.name ??
                    "—"
                  }
                />
                <ReviewRow label="Location" value={selectedOperationUnit?.name ?? "—"} />
                <ReviewRow label="Items" value={`${lines.length} item${lines.length === 1 ? "" : "s"}`} />
                <ReviewRow
                  label="Source budget"
                  value={
                    selectedSrcBudget?.name ??
                    (srcBudgetId != null && initialTransferFrom?.budgetId === srcBudgetId
                      ? (initial?.paymentRequestTransferFrom?.expenseBudget?.name ?? "—")
                      : "—")
                  }
                />
                <ReviewRow label="Total amount" value={formatAmount(total)} strong />
              </CardContent>
            </Card>
          </>
        )}

        {step === 3 && !isInternal && (
          <>
            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">Vendor &amp; payment</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Field label="Vendor">
                  <VendorCombobox
                    token={token ?? ""}
                    operationUnitId={operationUnitId}
                    value={vendor}
                    onChange={(v) => {
                      if ((v?.id ?? null) !== (vendor?.id ?? null)) {
                        // Bank accounts belong to the previous vendor — reset.
                        setBankAccounts(null);
                        setBankAccountId(null);
                      }
                      setVendor(v);
                      setVendorPan(v?.pan ?? "");
                    }}
                  />
                </Field>

                <Field label="Vendor PAN">
                  <Input
                    value={vendorPan}
                    readOnly
                    placeholder="Auto-filled from the vendor"
                    className="text-muted-foreground"
                  />
                </Field>

                <Field label="Mode of payment">
                  <IdSelect
                    options={initData.modeOfPayments}
                    value={modeOfPaymentId}
                    onChange={setModeOfPaymentId}
                    placeholder="Select mode of payment…"
                  />
                </Field>

                {isBankMode && vendor != null && (
                  <VendorBankDetails
                    accounts={bankAccounts}
                    loading={bankAccountsLoading}
                    selectedId={bankAccountId}
                    onSelect={setBankAccountId}
                  />
                )}

                <Field label="Priority">
                  <IdSelect
                    options={initData.priorities}
                    value={priorityId}
                    onChange={setPriorityId}
                    placeholder="Select priority…"
                  />
                </Field>

                <Field label="Note">
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What is this payment for?"
                    rows={3}
                  />
                </Field>

              </CardContent>
            </Card>

            <Card size="sm">
              <CardHeader>
                <CardTitle className="text-sm">Review</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-1.5 text-sm">
                <ReviewRow
                  label="Legal entity"
                  value={
                    initData.legalEntities.find((o) => o.id === legalEntityId)?.name ??
                    initData.legalEntities.find((o) => o.id === legalEntityId)?.label ??
                    initial?.legalEntity?.name ??
                    "—"
                  }
                />
                <ReviewRow label="Location" value={selectedOperationUnit?.name ?? "—"} />
                <ReviewRow label="Items" value={`${lines.length} item${lines.length === 1 ? "" : "s"}`} />
                <ReviewRow label="Total amount" value={formatAmount(total)} strong />
              </CardContent>
            </Card>
          </>
        )}

        {/* Back / Next pinned at the bottom of the form area. */}
        <div className="sticky bottom-0 z-30 mt-auto -mx-4 flex gap-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
          <Button
            type="button"
            variant="outline"
            onClick={() => goTo((step - 1) as Step)}
            disabled={step === 1 || saving || wizardFrozen}
          >
            Back
          </Button>
          {step < 3 ? (
            <Button
              type="button"
              className="flex-1"
              onClick={() => goTo((step + 1) as Step)}
              disabled={nextDisabled}
            >
              Next
            </Button>
          ) : (
            <Button
              type="button"
              className="flex-1"
              onClick={submit}
              disabled={saving || uploadBusy > 0 || !step3Valid || wizardFrozen}
            >
              {saving
                ? "Saving…"
                : uploadBusy > 0
                  ? "Uploading invoice…"
                  : mode === "edit"
                    ? "Save changes"
                    : "Submit request"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper header
// ---------------------------------------------------------------------------

function Stepper({
  step,
  labels,
  canGoTo,
  onGo,
}: {
  step: Step;
  /** Step labels for the active request kind (step ③ is "Vendor" or "Transfer from"). */
  labels: Record<Step, string>;
  canGoTo: (target: Step) => boolean;
  onGo: (target: Step) => void;
}) {
  const steps: Step[] = [1, 2, 3];
  return (
    <div className="sticky top-0 z-30 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur">
      <ol className="flex items-center">
        {steps.map((n, i) => {
          const state = n < step ? "done" : n === step ? "active" : "upcoming";
          const reachable = canGoTo(n);
          return (
            <li key={n} className={cn("flex items-center", i > 0 && "flex-1")}>
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "mx-1.5 h-px flex-1",
                    n <= step ? "bg-primary" : "bg-border"
                  )}
                />
              )}
              <button
                type="button"
                onClick={() => onGo(n)}
                disabled={!reachable || n === step}
                aria-current={n === step ? "step" : undefined}
                aria-label={`Step ${n}: ${labels[n]}`}
                className={cn(
                  "flex shrink-0 items-center gap-1.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  reachable && n !== step ? "cursor-pointer" : "cursor-default"
                )}
              >
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                    state === "done" && "bg-primary text-primary-foreground",
                    state === "active" &&
                      "bg-primary/15 text-primary ring-2 ring-primary",
                    state === "upcoming" && "bg-muted text-muted-foreground"
                  )}
                >
                  {state === "done" ? <CheckIcon className="size-3.5" /> : n}
                </span>
                <span
                  className={cn(
                    "text-xs font-medium",
                    state === "active"
                      ? "text-foreground"
                      : state === "done"
                        ? "text-primary"
                        : "text-muted-foreground"
                  )}
                >
                  {labels[n]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One amount line
// ---------------------------------------------------------------------------

function LineCard({
  index,
  line,
  token,
  departments,
  initialLine,
  canRemove,
  onDepartmentChange,
  onBudgetChange,
  onPatch,
  onRemove,
  onUploadBusyChange,
}: {
  index: number;
  line: FormLine;
  token: string;
  /** Departments of the step-① operation unit only. */
  departments: MyDepartment[];
  initialLine?: PaymentRequestAmountLine;
  canRemove: boolean;
  onDepartmentChange: (id: number | null) => void;
  onBudgetChange: (id: number | null) => void;
  onPatch: (patch: Partial<FormLine>) => void;
  onRemove: () => void;
  onUploadBusyChange: (busy: boolean) => void;
}) {
  const departmentOptions: ComboboxOption[] = departments.map((d) => ({
    id: d.id,
    name: d.name ?? d.label ?? String(d.id),
  }));
  // Edit mode: keep the prefilled department displayable even when it is missing from the
  // filtered list (e.g. access was revoked since the request was raised).
  if (
    line.departmentId != null &&
    !departmentOptions.some((o) => o.id === line.departmentId) &&
    initialLine?.department?.id === line.departmentId
  ) {
    departmentOptions.unshift({
      id: line.departmentId,
      name: initialLine.department?.name ?? `Department #${line.departmentId}`,
    });
  }

  // Requestable items only; while the list is still loading in edit mode, fall back to the
  // prefilled budget so the combobox can display it.
  const budgetOptions: ComboboxOption[] = (line.budgetItems ?? [])
    .filter(enabled)
    .map((b) => ({ id: b.id, name: b.name }));
  if (
    line.budgetId != null &&
    !budgetOptions.some((o) => o.id === line.budgetId) &&
    initialLine?.expenseBudget?.id === line.budgetId
  ) {
    budgetOptions.unshift({
      id: line.budgetId,
      name: initialLine.expenseBudget?.name ?? `Budget #${line.budgetId}`,
    });
  }

  const approverOptions: ComboboxOption[] = (line.approvers ?? []).map((a) => ({
    id: a.id,
    name: a.displayNameWithTitle ?? String(a.id),
  }));
  if (
    line.approverId != null &&
    !approverOptions.some((o) => o.id === line.approverId) &&
    initialLine?.reqApprovedBy?.id === line.approverId
  ) {
    approverOptions.unshift({
      id: line.approverId,
      name: initialLine.reqApprovedBy?.displayNameWithTitle ?? `Approver #${line.approverId}`,
    });
  }

  const selectedBudget = (line.budgetItems ?? []).find((b) => b.id === line.budgetId);
  const overBalance = isLineOverBalance(line);
  const allowedMax = lineAllowedMax(line);

  return (
    <Card size="sm">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm">Amount line {index + 1}</CardTitle>
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove amount line ${index + 1}`}
              onClick={onRemove}
            >
              <Trash2 className="text-destructive" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Field label="Department">
          <Combobox
            options={departmentOptions}
            value={line.departmentId}
            onValueChange={onDepartmentChange}
            placeholder="Select department…"
            emptyText="No departments in this location."
          />
        </Field>

        <Field
          label="Budget"
          hint={
            line.budgetLoading
              ? "Loading budget items…"
              : selectedBudget
                ? `Available balance: ${formatAmount(selectedBudget.availableAmount)}`
                : line.departmentId == null
                  ? "Select a department first."
                  : undefined
          }
        >
          <Combobox
            options={budgetOptions}
            value={line.budgetId}
            onValueChange={onBudgetChange}
            placeholder={line.budgetLoading ? "Loading…" : "Select budget…"}
            disabled={line.departmentId == null || line.budgetLoading}
            emptyText="No requestable budget items."
          />
        </Field>

        <Field
          label="Approver"
          hint={line.approverLoading ? "Loading approvers…" : undefined}
        >
          <Combobox
            options={approverOptions}
            value={line.approverId}
            onValueChange={(id) => onPatch({ approverId: id })}
            placeholder={line.approverLoading ? "Loading…" : "Select approver…"}
            disabled={line.budgetId == null || line.approverLoading}
            emptyText="No approvers for this budget."
          />
        </Field>

        {(() => {
          // Same field as the Angular form's per-line "Tag" select: always visible, optional,
          // options = the selected budget's tag list. A prefilled tag missing from the loaded
          // list (edit mode / stale list) stays displayable.
          const tagOptions =
            line.tag && !line.tags.includes(line.tag) ? [line.tag, ...line.tags] : line.tags;
          const noTags = line.budgetId != null && !line.approverLoading && tagOptions.length === 0;
          return (
            <Field
              label="Tag (optional)"
              hint={noTags ? "No tags defined for this budget." : undefined}
            >
              <Select
                items={[
                  { value: "", label: "No tag" },
                  ...tagOptions.map((t) => ({ value: t, label: t })),
                ]}
                value={line.tag ?? ""}
                onValueChange={(v) => onPatch({ tag: v ? String(v) : null })}
                disabled={line.budgetId == null || noTags}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No tag</SelectItem>
                  {tagOptions.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          );
        })()}

        <Field
          label="Amount"
          error={
            overBalance && allowedMax != null
              ? `Exceeds available balance of ${formatAmount(allowedMax)}`
              : undefined
          }
        >
          <Input
            inputMode="decimal"
            value={line.amount}
            onChange={(e) => {
              const v = e.target.value;
              if (/^\d*\.?\d{0,2}$/.test(v)) onPatch({ amount: v });
            }}
            placeholder="0.00"
            aria-invalid={overBalance || undefined}
            className={cn(overBalance && "border-destructive")}
          />
        </Field>

        <Field label="Remarks">
          <Input
            value={line.remarks}
            onChange={(e) => onPatch({ remarks: e.target.value })}
            placeholder="Optional remarks"
          />
        </Field>

        <Field label="Invoices">
          <InvoiceCapture
            token={token}
            amountId={line.id}
            attachments={line.attachments}
            onChange={(attachments) => onPatch({ attachments })}
            onBusyChange={onUploadBusyChange}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Vendor bank details (bank payment modes)
// ---------------------------------------------------------------------------

/**
 * Selectable list of the vendor's bank accounts, shown under Mode of payment for bank modes
 * (Cheque/NEFT/RTGS). accounts === null means not loaded (loading or load failed).
 */
function VendorBankDetails({
  accounts,
  loading,
  selectedId,
  onSelect,
}: {
  accounts: VendorBankAccount[] | null;
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <Field label="Vendor bank details">
      {loading || accounts === null ? (
        <p className="text-xs text-muted-foreground">Loading bank accounts…</p>
      ) : accounts.length === 0 ? (
        <div className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-100 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <CircleAlert className="mt-0.5 size-4 shrink-0" />
          No bank account on record for this vendor — accounts team will capture it during
          processing
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {accounts.map((a) => {
            const selected = a.id === selectedId;
            return (
              <button
                type="button"
                key={a.id}
                onClick={() => onSelect(a.id)}
                aria-pressed={selected}
                className={cn(
                  "relative flex w-full flex-col gap-0.5 rounded-lg border p-2.5 pr-9 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                  selected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <span className="flex flex-wrap items-center gap-1.5 font-medium">
                  <span className="truncate">{a.bank?.name ?? "Bank"}</span>
                  {a.isDefault && <Badge variant="secondary">Default</Badge>}
                  {a.bankAcVerified ? (
                    <Badge className="border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                      Verified
                    </Badge>
                  ) : (
                    <Badge className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                      Unverified
                    </Badge>
                  )}
                </span>
                <span>A/c: {a.bankAccount ?? "—"}</span>
                <span className="text-xs text-muted-foreground">
                  IFSC: {a.ifscCode ?? "—"}
                  {a.bankBranch ? ` · ${a.bankBranch}` : ""}
                </span>
                {a.accountHolderName && (
                  <span className="text-xs text-muted-foreground">{a.accountHolderName}</span>
                )}
                {selected && (
                  <CheckIcon className="absolute top-2.5 right-2.5 size-4 text-primary" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  /** Inline validation message — shown (in destructive style) instead of the hint. */
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive">{error}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

function ReviewRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("min-w-0 truncate text-right", strong && "font-semibold")}>{value}</span>
    </div>
  );
}

/** Simple id-labeled Select over initData option lists ({id, label|name}). */
function IdSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: NamedItem[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder: string;
}) {
  const items = options.map((o) => ({
    value: String(o.id),
    label: o.label ?? o.name ?? String(o.id),
  }));
  return (
    <Select
      items={items}
      value={value != null ? String(value) : null}
      onValueChange={(v) => onChange(v ? Number(v) : null)}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Server-searched vendor picker: debounced (300ms) apiVendorSearch scoped to the step-①
 * operation unit. Client-side filtering is disabled — the server already applies the
 * multi-word match (HqlSearch.likeAllWords).
 */
function VendorCombobox({
  token,
  operationUnitId,
  value,
  onChange,
}: {
  token: string;
  operationUnitId: number | null;
  value: VendorOption | null;
  onChange: (v: VendorOption | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<VendorOption[]>([]);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!token || operationUnitId == null) {
      setOptions([]);
      return;
    }
    const seq = ++seqRef.current;
    const t = setTimeout(() => {
      searchVendors(token, operationUnitId, query)
        .then((v) => {
          if (seq === seqRef.current) setOptions(v);
        })
        .catch(() => {
          // Keep the previous options on transient errors.
        });
    }, 300);
    return () => clearTimeout(t);
  }, [token, operationUnitId, query]);

  // Keep the selected vendor visible even when the current search misses it.
  const items = useMemo(() => {
    if (value && !options.some((o) => o.id === value.id)) return [value, ...options];
    return options;
  }, [options, value]);

  const disabled = operationUnitId == null;

  return (
    <ComboboxPrimitive.Root
      items={items}
      value={value}
      onValueChange={(v) => onChange(v ?? null)}
      onInputValueChange={(input) => setQuery(input)}
      itemToStringLabel={(o: VendorOption | null) => o?.name ?? ""}
      isItemEqualToValue={(a: VendorOption | null, b: VendorOption | null) => a?.id === b?.id}
      filter={() => true}
      disabled={disabled}
    >
      <div className="relative">
        <ComboboxPrimitive.Input
          placeholder={disabled ? "Select a location first" : "Search vendors…"}
          disabled={disabled}
          data-slot="combobox-input"
          className="flex h-9 w-full items-center rounded-lg border border-input bg-transparent py-2 pr-9 pl-2.5 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
        />
        <ComboboxPrimitive.Trigger
          disabled={disabled}
          aria-label="Open"
          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground outline-none disabled:opacity-50"
        >
          <ComboboxPrimitive.Icon render={<ChevronsUpDownIcon className="size-4 shrink-0" />} />
        </ComboboxPrimitive.Trigger>
      </div>

      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner sideOffset={4} className="isolate z-50">
          <ComboboxPrimitive.Popup
            data-slot="combobox-content"
            className="max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
          >
            <ComboboxPrimitive.Empty className="px-2 py-4 text-center text-sm text-muted-foreground">
              No vendors found.
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List>
              {(option: VendorOption) => (
                <ComboboxPrimitive.Item
                  key={option.id}
                  value={option}
                  className="relative flex w-full cursor-default flex-col rounded-md py-1.5 pr-8 pl-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                >
                  <span className="flex items-center gap-1.5 truncate">
                    {option.name}
                    {option.verifiedForPayment && (
                      <CheckIcon className="size-3.5 shrink-0 text-green-600 dark:text-green-400" />
                    )}
                  </span>
                  {option.pan && (
                    <span className={cn("truncate text-xs text-muted-foreground")}>
                      PAN: {option.pan}
                    </span>
                  )}
                  <ComboboxPrimitive.ItemIndicator className="absolute top-2 right-2 flex items-center">
                    <CheckIcon className="size-4" />
                  </ComboboxPrimitive.ItemIndicator>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  );
}
